/**
 * Wrapper component for labs that use the project system.
 * This component will create a project manager for the current level and script
 * or channel, and will clean up the project manager on level change or unmount.
 */

import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';

import header from '@cdo/apps/code-studio/header';
import {clearHeader} from '@cdo/apps/code-studio/headerRedux';
import {
  getCurrentScriptLevelId,
  getLevelPropertiesPath,
  getUserAppOptionsPath,
} from '@cdo/apps/code-studio/progressReduxSelectors';
import useLifecycleNotifier from '@cdo/apps/lab2/hooks/useLifecycleNotifier';
import {
  isReadOnlyWorkspace,
  setUpWithLevel,
  setUpWithoutLevel,
  shouldHideShareAndRemix,
} from '@cdo/apps/lab2/lab2Redux';
import Lab2Registry from '@cdo/apps/lab2/Lab2Registry';
import {resetProjectMetadata} from '@cdo/apps/lab2/redux/lab2ProjectRedux';
import {AppName} from '@cdo/apps/lab2/types';
import {LifecycleEvent} from '@cdo/apps/lab2/utils';
import {useAppDispatch, useAppSelector} from '@cdo/apps/util/reduxHooks';

const ProjectContainer: React.FunctionComponent<ProjectContainerProps> = ({
  children,
  channelId,
  appName,
}) => {
  const currentLevelId = useAppSelector(state => state.progress.currentLevelId);
  const userId = useAppSelector(
    state => state.progress.viewAsUserId || undefined
  );
  const scriptId = useAppSelector(
    state => state.progress.scriptId || undefined
  );
  const scriptLevelId = useSelector(getCurrentScriptLevelId);

  const isStandaloneProjectLevel = useAppSelector(
    state => state.lab.levelProperties?.isProjectLevel
  );
  const hideShareAndRemix = useSelector(shouldHideShareAndRemix);
  const loadedChannelId = useAppSelector(
    state => state.lab.channel && state.lab.channel.id
  );
  const isOwnerOfChannel = useAppSelector(
    state => state.lab.channel && state.lab.channel.isOwner
  );

  const levelPropertiesPath = useSelector(getLevelPropertiesPath);
  const userAppOptionsPath = useSelector(getUserAppOptionsPath);

  const dispatch = useAppDispatch();
  const isReadOnly = useAppSelector(isReadOnlyWorkspace);

  // When the level changes, reset metadata relating to the project in redux.
  useLifecycleNotifier(LifecycleEvent.LevelLoadStarted, () =>
    dispatch(resetProjectMetadata())
  );

  useEffect(() => {
    // The redux types are very complicated, so in order to re-use this variable
    // we are defining it as any.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let promise: any;
    // Before loading, clear the header so we don't accidentally show share and remix
    // for a level that does not allow it.
    dispatch(clearHeader());
    if (currentLevelId && levelPropertiesPath) {
      // If we have a level id, set up the lab with that level. If we also have a channel id,
      // we will load the project based on that channel id, otherwise we will look up a channel id
      // for the level.
      promise = dispatch(
        setUpWithLevel({
          levelId: parseInt(currentLevelId),
          userId,
          scriptId,
          scriptLevelId,
          levelPropertiesPath,
          userAppOptionsPath,
          channelId,
        })
      );
    } else if (channelId && appName) {
      // Otherwise, if we have a channel id, set up the lab using the channel id.
      // This path should only be used for lab pages that don't have a level, such as
      // /projectbeats previously. App name also must be provided if using this path.
      promise = dispatch(setUpWithoutLevel({channelId, appName}));
    }
    return () => {
      // If we have an early return, we will abort the promise in progress.
      // An early return could happen if the level is changed mid-load.
      promise?.abort();
    };
  }, [
    channelId,
    appName,
    currentLevelId,
    scriptId,
    scriptLevelId,
    levelPropertiesPath,
    userAppOptionsPath,
    dispatch,
    userId,
  ]);

  useEffect(() => {
    window.addEventListener('beforeunload', event => {
      const projectManager = Lab2Registry.getInstance().getProjectManager();
      // Force a save before the page unloads, if there are unsaved changes.
      // If we need to force a save, prevent navigation so we can save first.
      // We skip this if we are already force reloading, as that will occur when
      // saving already encountered an issue. We also can skip this in read only mode,
      // as we never save in read only mode.
      if (
        projectManager &&
        !projectManager.isForceReloading() &&
        projectManager.hasUnsavedChanges() &&
        !isReadOnly
      ) {
        projectManager.cleanUp();
        event.preventDefault();
        event.returnValue = '';
      }
    });
  }, [isReadOnly]);

  useEffect(() => {
    // Ensure the header is cleared when we have a change,
    // then possibly load a new header if the level has one.
    dispatch(clearHeader());
    // If there is no channel, we can't load a header.
    if (loadedChannelId && isOwnerOfChannel) {
      if (isStandaloneProjectLevel) {
        // Standalone projects see project header (includes rename option).
        // Standalone projects always show share and remix.
        header.showProjectHeader();
      } else {
        // Project backed levels see project backed header, which can
        // conditionally show share and remix.
        header.showHeaderForProjectBacked({
          showShareAndRemix: !hideShareAndRemix,
        });
      }
    } else if (
      loadedChannelId &&
      !isOwnerOfChannel &&
      isStandaloneProjectLevel
    ) {
      // If we are viewing another user's project, and this is a standalone
      // project, show the minimal project header (project name and remix button).
      header.showMinimalProjectHeader();
    }
  }, [
    hideShareAndRemix,
    isStandaloneProjectLevel,
    loadedChannelId,
    isOwnerOfChannel,
    dispatch,
  ]);

  return <>{children}</>;
};

interface ProjectContainerProps {
  children: React.ReactNode;
  /** Channel ID for the project, if already known. Used for standalone projects and projects without levels. */
  channelId?: string;
  /**
   * App name for the lab that will be displayed, used only for projects without levels. Must be provided
   * if loading a lab without a level.
   */
  appName?: AppName;
}

export default ProjectContainer;
